import { useState, useRef, useEffect, useCallback } from 'react';
import { createVmAction, fetchOperation } from '../services/esxiApi';
import {
  OPERATION_POLL_INTERVAL_MS,
  OPERATION_STATUS,
  isTerminalOperation,
} from '../utils/constants';
import { isVmLocked } from '../utils/format';

/**
 * Sanitizza un messaggio d'errore proveniente dal backend, evitando di mostrare
 * output SSH grezzo o dettagli interni. Ricade su messaggi per codice noto.
 */
function humanizeOperationError(op, fallbackMessage) {
  const code = op?.error?.code || op?.errorCode || op?.error;
  const map = {
    ESXI_INVALID_POWER_STATE:
      'Lo stato iniziale della VM non è compatibile con l’azione richiesta.',
    ESXI_TOOLS_NOT_RUNNING:
      'Spegnimento guest non disponibile: VMware Tools non è attivo.',
    ESXI_UNAVAILABLE: 'Host ESXi non raggiungibile.',
    ESXI_NOT_CONFIGURED: 'Host ESXi non configurato.',
    ESXI_VM_BUSY: 'Un’altra operazione è già in corso su questa VM.',
    ESXI_VM_NOT_FOUND: 'VM non presente nel catalogo.',
  };
  if (typeof code === 'string' && map[code]) return map[code];
  const msg = op?.error?.message || op?.message;
  if (msg && typeof msg === 'string') return msg;
  return fallbackMessage || 'Operazione non riuscita.';
}

/**
 * Gestisce il ciclo di vita delle azioni powerOn/shutdown su singole VM:
 *
 * - blocca la VM coinvolta (lock per catalogId) per impedire doppi click;
 * - dopo la POST NON considera conclusa l'azione: fa polling moderato di
 *   `/operations/:id` fino a SUCCEEDED / FAILED / TIMED_OUT;
 * - a conclusione invoca `onSettled` (per aggiornare l'inventario);
 * - annulla timer e richieste allo smontaggio.
 *
 * @param {Function} onSettled callback chiamata quando un'operazione termina.
 */
export function useVmAction(onSettled) {
  // Stato per VM: { operationId, status, action, vmName, phase, error }
  // phase: 'submitting' | 'polling' | 'done'
  const [operations, setOperations] = useState({});

  const mountedRef = useRef(true);
  const timersRef = useRef(new Map());
  const abortsRef = useRef(new Map());
  const onSettledRef = useRef(onSettled);

  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  const clearTimerFor = useCallback((catalogId) => {
    const t = timersRef.current.get(catalogId);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(catalogId);
    }
  }, []);

  const abortFor = useCallback((catalogId) => {
    const c = abortsRef.current.get(catalogId);
    if (c) {
      c.abort();
      abortsRef.current.delete(catalogId);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const timers = timersRef.current;
    const aborts = abortsRef.current;
    return () => {
      mountedRef.current = false;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      aborts.forEach((c) => c.abort());
      aborts.clear();
    };
  }, []);

  const setOp = useCallback((catalogId, patch) => {
    setOperations((prev) => ({
      ...prev,
      [catalogId]: { ...prev[catalogId], ...patch },
    }));
  }, []);

  const poll = useCallback(
    (catalogId, operationId) => {
      if (!mountedRef.current) return;
      abortFor(catalogId);
      const controller = new AbortController();
      abortsRef.current.set(catalogId, controller);

      fetchOperation(operationId, controller.signal)
        .then((op) => {
          if (!mountedRef.current) return;
          const status = op?.status;
          if (isTerminalOperation(status)) {
            clearTimerFor(catalogId);
            setOp(catalogId, {
              status,
              phase: 'done',
              error:
                status === OPERATION_STATUS.SUCCEEDED
                  ? null
                  : humanizeOperationError(op),
            });
            if (onSettledRef.current) {
              onSettledRef.current(catalogId, {
                ...op,
                status,
              });
            }
          } else {
            setOp(catalogId, { status: status || OPERATION_STATUS.RUNNING });
            clearTimerFor(catalogId);
            timersRef.current.set(
              catalogId,
              setTimeout(
                () => poll(catalogId, operationId),
                OPERATION_POLL_INTERVAL_MS,
              ),
            );
          }
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          if (!mountedRef.current) return;
          // Un errore transitorio non deve fermare la verifica: riprova.
          clearTimerFor(catalogId);
          timersRef.current.set(
            catalogId,
            setTimeout(
              () => poll(catalogId, operationId),
              OPERATION_POLL_INTERVAL_MS,
            ),
          );
        });
    },
    [abortFor, clearTimerFor, setOp],
  );

  /**
   * Avvia un'azione su una VM. Idempotente rispetto ai doppi click: se la VM è
   * già occupata, la chiamata viene ignorata.
   * @returns {boolean} true se l'azione è stata avviata, false se ignorata.
   */
  const submit = useCallback(
    (catalogId, action, reason, vmName) => {
      if (isVmLocked(operations, catalogId)) return false; // anti doppio-click

      setOp(catalogId, {
        operationId: null,
        status: OPERATION_STATUS.QUEUED,
        action,
        vmName,
        phase: 'submitting',
        error: null,
      });

      abortFor(catalogId);
      const controller = new AbortController();
      abortsRef.current.set(catalogId, controller);

      createVmAction(catalogId, action, reason, controller.signal)
        .then((op) => {
          if (!mountedRef.current) return;
          const operationId = op?.id || op?.operationId;
          setOp(catalogId, {
            operationId,
            status: op?.status || OPERATION_STATUS.QUEUED,
            phase: 'polling',
          });
          if (operationId) poll(catalogId, operationId);
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          if (!mountedRef.current) return;
          const data = err?.response?.data;
          setOp(catalogId, {
            status: OPERATION_STATUS.FAILED,
            phase: 'done',
            error: humanizeOperationError(
              data,
              'Impossibile avviare l’operazione.',
            ),
          });
        });

      return true;
    },
    [operations, setOp, abortFor, poll],
  );

  /** Rimuove lo stato dell'operazione di una VM (dopo aver mostrato l'esito). */
  const dismiss = useCallback(
    (catalogId) => {
      clearTimerFor(catalogId);
      abortFor(catalogId);
      setOperations((prev) => {
        const next = { ...prev };
        delete next[catalogId];
        return next;
      });
    },
    [clearTimerFor, abortFor],
  );

  /** True se la VM ha un'operazione in corso (lock attivo). */
  const isBusy = useCallback(
    (catalogId) => isVmLocked(operations, catalogId),
    [operations],
  );

  return { operations, submit, dismiss, isBusy };
}
